import { clsx } from "clsx";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Select } from "@/components/common";

interface UsersPaginationProps {
  page: number;
  pageSize: number;
  totalCount: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (pageSize: number) => void;
}

export function UsersPagination({
  page,
  pageSize,
  totalCount,
  totalPages,
  onPageChange,
  onPageSizeChange,
}: UsersPaginationProps) {
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, totalCount);

  function getPageNumbers(): (number | "ellipsis-start" | "ellipsis-end")[] {
    const pages: (number | "ellipsis-start" | "ellipsis-end")[] = [];

    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
      return pages;
    }

    pages.push(1);

    if (page > 3) {
      pages.push("ellipsis-start");
    }

    const rangeStart = Math.max(2, page - 1);
    const rangeEnd = Math.min(totalPages - 1, page + 1);

    for (let i = rangeStart; i <= rangeEnd; i++) {
      pages.push(i);
    }

    if (page < totalPages - 2) {
      pages.push("ellipsis-end");
    }

    pages.push(totalPages);

    return pages;
  }

  if (totalCount === 0) {
    return null;
  }

  return (
    <div className="flex flex-col items-center justify-between gap-3 border-t border-edge px-1 py-3 sm:flex-row">
      <div className="flex items-center gap-2 text-sm text-dim">
        <span>
          {start}–{end} z {totalCount}
        </span>
        <Select
          size="sm"
          value={String(pageSize)}
          onChange={(v) => {
            onPageSizeChange(Number(v));
          }}
          options={[
            { value: "10", label: "10 / str." },
            { value: "25", label: "25 / str." },
            { value: "50", label: "50 / str." },
          ]}
        />
      </div>

      <div className="flex items-center gap-1">
        <button
          type="button"
          disabled={page <= 1}
          onClick={() => {
            onPageChange(page - 1);
          }}
          className="rounded-lg p-1.5 text-faint transition-colors hover:bg-muted hover:text-sub disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Poprzednia strona"
        >
          <ChevronLeft size={16} />
        </button>

        {getPageNumbers().map((pageNum) =>
          typeof pageNum === "string" ? (
            <span key={pageNum} className="px-1 text-sm text-faint">
              ...
            </span>
          ) : (
            <button
              key={pageNum}
              type="button"
              onClick={() => {
                onPageChange(pageNum);
              }}
              className={clsx(
                "min-w-[32px] rounded-lg px-2 py-1 text-sm font-medium transition-colors",
                pageNum === page
                  ? "bg-adara-700 text-white"
                  : "text-sub hover:bg-muted",
              )}
            >
              {pageNum}
            </button>
          ),
        )}

        <button
          type="button"
          disabled={page >= totalPages}
          onClick={() => {
            onPageChange(page + 1);
          }}
          className="rounded-lg p-1.5 text-faint transition-colors hover:bg-muted hover:text-sub disabled:cursor-not-allowed disabled:opacity-50"
          aria-label="Następna strona"
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
